import dayjs from 'dayjs';
import { t } from 'i18next';

import { NumberHelper } from 'core/helpers';
import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { IMargin } from 'pricing/types';
import { PriceBasedOn, PricingStrategy } from 'quotes/enums';
import { QuotePartHelper, QuotePartPricingHelper } from 'quotes/helpers';
import { ICalculatePriceForm, ICustomerQuotePart } from 'quotes/types';
import { getCostValidityDefaults } from 'quotes/utils';

export const getDefaultValues = (
  selectedQuoteParts: ICustomerQuotePart[],
  presetMargins?: IMargin[]
): ICalculatePriceForm => {
  if (!selectedQuoteParts.length) {
    return {
      id: null,
      margin: -1,
      customMargin: null,
      cost: 0,
      purchaseCost: null,
      isCostingCustom: false,
      isPricingCustom: false,
      costSource: {
        vendorQuoteNumber: null,
        validSince: null,
        validUntil: null,
      },
      isReplacementCostCustom: false,
      replacementCost: null,
      replacementCostNumber: null,
      shipmentType: null,
      priceBasedOn: PriceBasedOn.QUOTED,
      pricingMargin: null,
      trueMargin: null,
      replacementStandardCost: null,
      pricingDate: null,
      selectedSpa: null,
    };
  }

  const firstSelectedPart = selectedQuoteParts[0];

  const isReplacementMode = firstSelectedPart?.priceBasedOn === PriceBasedOn.REPLACEMENT;
  const activeCost = isReplacementMode
    ? firstSelectedPart?.replacementStandardCost ??
      firstSelectedPart?.externalReplacementStandardCost
    : firstSelectedPart?.computedCost;
  const pricingMarginValue = PricingHelper.computeGpm(activeCost, firstSelectedPart?.price);

  const isCustomMargin = !presetMargins?.find((m) =>
    NumberHelper.areEqual(m.percentage, pricingMarginValue * 100)
  );

  // Outside the alternate branch: the save mapper reads the quote's flag, and seeding from the
  // line's would let a margin-only save drop a held pick.
  const selectedSpa =
    1 === selectedQuoteParts?.length
      ? QuotePartPricingHelper.mapQuotePartToSelectedSpa(firstSelectedPart)
      : null;

  let alternateVersionValues = {};

  if (
    selectedQuoteParts?.length > 1 &&
    selectedQuoteParts.some((quotePart) => quotePart?.alternateVersionEnabled)
  ) {
    alternateVersionValues = {
      priceBasedOn: QuotePartHelper.getConsensusPriceBasedOn(selectedQuoteParts),
    };
  }

  if (selectedQuoteParts?.length === 1 && Boolean(firstSelectedPart?.alternateVersionEnabled)) {
    const costSourceVendorQuoteNumber = firstSelectedPart
      ? QuotePartHelper.getVendorQuoteNumber(firstSelectedPart)
      : null;

    alternateVersionValues = {
      // A selected-SPA line is never manually costed, whatever the rest of the row says.
      isCostingCustom:
        !selectedSpa &&
        (PricingSourceType.CUSTOM === firstSelectedPart?.costSourceType ||
          !firstSelectedPart?.purchaseCost),
      isPricingCustom: PricingSourceType.CUSTOM === firstSelectedPart?.priceSourceType,
      prioritizeLegacyCost: !!firstSelectedPart?.prioritizeLegacyCost,
      costSource: {
        vendorQuoteNumber: costSourceVendorQuoteNumber,
        ...getCostValidityDefaults(firstSelectedPart),
      },
      isReplacementCostCustom:
        !firstSelectedPart?.externalReplacementCost ||
        (!!firstSelectedPart?.replacementCost &&
          firstSelectedPart?.externalReplacementCost !== firstSelectedPart?.replacementCost),
      replacementCost:
        firstSelectedPart?.replacementCost ?? firstSelectedPart?.externalReplacementCost,
      replacementCostNumber:
        firstSelectedPart?.replacementCostNumber ??
        firstSelectedPart?.externalRepCostRegistrationNum,
      companyManufacturerName: firstSelectedPart?.companyManufacturerName,
      companyManufacturerId: firstSelectedPart?.companyManufacturerId,
      accountsPayableId: firstSelectedPart?.accountsPayableId,
      priceBasedOn: firstSelectedPart?.priceBasedOn || PriceBasedOn.QUOTED,
      // Deferred lifecycle: only seed the standard cost for replacement-mode lines (the BE keeps
      // it fresh at its need-points). In quoted mode a preserved row value may be stale, so leave
      // it null and let the drawer show the deferred placeholder.
      replacementStandardCost: isReplacementMode
        ? Number(
            firstSelectedPart?.replacementStandardCost ??
              firstSelectedPart?.externalReplacementStandardCost
          ) || null
        : null,
      pricingMargin: PricingHelper.computeGpmPercentage(activeCost, firstSelectedPart?.price),
      trueMargin: PricingHelper.computeGpmPercentage(
        firstSelectedPart?.computedCost,
        firstSelectedPart?.price
      ),
      pricingDate: firstSelectedPart?.pricingDate ? dayjs(firstSelectedPart.pricingDate) : null,
      pricingStrategy: firstSelectedPart?.pricingStrategy ?? PricingStrategy.EXTERNAL,
    };
  }

  return {
    // Seeded on every path: yup casts an absent nullable object to `{}`, read as a pick.
    selectedSpa,
    id: selectedQuoteParts.length === 1 ? firstSelectedPart?.id : null,
    price: firstSelectedPart?.price,
    margin: isCustomMargin ? -1 : NumberHelper.round(pricingMarginValue || -1),
    customMargin: isCustomMargin ? pricingMarginValue * 100 : null,
    cost: selectedQuoteParts?.length > 1 ? -1 : firstSelectedPart?.computedCost,
    purchaseCost: selectedQuoteParts?.length > 1 ? -1 : firstSelectedPart?.computedPurchaseCost,
    shipmentType: firstSelectedPart?.shipmentType,
    corporateLocation: firstSelectedPart?.corporateLocation,
    ...alternateVersionValues,
  };
};

export const mapMarginsToRadioChoices = (margins: IMargin[] = []) => {
  const savedMargins = margins.map((margin) => ({
    value: margin.percentage / 100,
    label: `${margin.percentage}%`,
  }));

  return [
    ...savedMargins,
    {
      value: -1,
      label: t('labelCustom'),
    },
  ];
};
